import BankIDSwedenLogo from "@/assets/img/BankIDSwedenLogo.svg";
import BankIDSwedenLogoDark from "@/assets/img/BankIDSwedenLogoDark.svg";
import BankIDSymbol from "@/assets/img/BankIDSymbol.svg";
import BankIDSymbolDark from "@/assets/img/BankIDSymbolDark.svg";
import FinlandFlag from "@/assets/img/FinlandFlag.svg";
import GoogleLogo from "@/assets/img/GoogleLogo.svg";
import MicrosoftLogo from "@/assets/img/MicrosoftLogo.svg";
import MitIDDenmark from "@/assets/img/MitIdDenmarkLogo.svg";
import MitIDDenmarkDark from "@/assets/img/MitIdDenmarkLogoDark.svg";
import { StringFieldModel } from "@/common/commonForms";
import { Alert } from "@/components-v2/Alert";
import { Box, Container, Grid, GridItem, VBox, VBoxProps } from "@/components-v2/Box";
import { Button, LinkButton } from "@/components-v2/Button";
import Form from "@/components-v2/Form";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import { LabelledDivider } from "@/components-v2/LabelledDivider";
import { Spinner } from "@/components-v2/LoadingIndicator";
import { MTextInput } from "@/components-v2/TextInput";
import { isTMessage, TMessage } from "@/i18n/tt";
import { useTranslation } from "@/stores/RootStoreContext";
import { isMfaChallenge, MfaChallenge } from "@/stores/UserStore";
import { Markdown } from "@/text/Markdown";
import { classNames } from "@/utils/classNames";
import { isMobileView } from "@/utils/screen";
import { compact } from "lodash";
import { observer } from "mobx-react";
import { FC, ReactNode, useEffect } from "react";
import { LoginAppBar } from "./LoginAppBar";
import { CreateUserModel, isSsoConnectedProvider, LoginMethod, LoginModel } from "./loginDomain";
import { logDebug } from "@/utils/log.ts";

export const ContentBox = observer(({ children, ...props }: VBoxProps) => {
  return (
    <VBox className="max-w-100" container paddingX={false} {...props}>
      {children}
    </VBox>
  );
});

export const LoginHeader = observer(
  ({ heading, intro }: { heading: TMessage; intro?: TMessage | ReactNode }) => {
    const { t } = useTranslation();
    return (
      <ContentBox>
        <h2 className="text-h3">{t(heading)}</h2>
        {isTMessage(intro) ? <Markdown value={t(intro)} enableLinks /> : intro}
      </ContentBox>
    );
  },
);

export const LoginLayout = observer(
  (props: { onBack?: () => void; children?: ReactNode | undefined }) => {
    const { t, lang } = useTranslation();
    const isMainPage = !props.onBack;
    const iframeUrl = `https://dcompany-public-website.vercel.app/promo-${lang}`;
    logDebug("iframeUrl:", iframeUrl);
    return (
      <FullScreenLayout
        centerPanelContainerProps={{ className: "relative w-[37.5%]" }}
        centerPanel={
          <FullScreenLayout
            bg
            appBar={(!isMobileView() || isMainPage) && <LoginAppBar />}
            centerPanel={
              <>
                <Container
                  container
                  maxWidth={"sm"}
                  gap={"xl"}
                  rounded={"lg"}
                  className="pt-8 py-16"
                  marginTop={!isMobileView() && "lg"}
                >
                  {props.onBack && (
                    <ContentBox>
                      <div>
                        <LinkButton label={t("back")} arrow={"left"} onClick={props.onBack} />
                      </div>
                    </ContentBox>
                  )}
                  {props.children}
                </Container>
              </>
            }
          />
        }
        rightPanelContainerProps={{
          bg: false,
          borderLeft: false,
          overflowY: undefined,
          grow: true,
          className: "bg-gray-x-hard relative w-[62.5%] hidden xl:flex",
        }}
        rightPanel={
          <iframe
            src={iframeUrl}
            style={{ position: "absolute", height: "100%", width: "100%" }}
            height="100%"
            width="100%"
          />
        }
      />
    );
  },
);

export type LoginMethodIcon = { src: string; darkSrc?: string };

export const LoginMethodLogo = ({ icon, alt }: { icon: LoginMethodIcon; alt: string }) =>
  icon.darkSrc ? (
    <>
      <img src={icon.src} alt={alt} className="h-5 hide-on-dark" />
      <img src={icon.darkSrc} alt={alt} className="h-5 show-on-dark" />
    </>
  ) : (
    <img src={icon.src} alt={alt} className="h-5" />
  );

export const LoginMethodButton = observer(
  ({
    id,
    icon,
    label,
    onClick,
    loading,
  }: {
    id: string;
    icon: LoginMethodIcon;
    label: TMessage;
    onClick: () => void;
    loading?: boolean;
  }) => {
    const { t } = useTranslation();
    return (
      <button
        id={id}
        onClick={onClick}
        className={classNames(
          "group",
          "flex",
          "rounded-sm",
          "border",
          "p-4",
          "font-semibold",
          "border-primary",
          "color-primary",
          "hover:border-primary-dark",
          "hover:color-primary-dark",
        )}
      >
        <Grid grow alignItems="center">
          <GridItem colSpan={3}>
            <Box justifyContent="center" alignItems="center">
              {loading ? <Spinner /> : <LoginMethodLogo icon={icon} alt={t(label)} />}
            </Box>
          </GridItem>
          <GridItem colSpan={9}>{t(label)}</GridItem>
        </Grid>
      </button>
    );
  },
);

const LoginWithUsername = observer(
  ({ username, onContinue }: { username: StringFieldModel; onContinue: () => void }) => {
    return (
      <Form onSubmit={username.isEmpty ? undefined : onContinue}>
        <VBox>
          <MTextInput
            name={"username"}
            label={{ key: "login.username" }}
            model={username}
            autoComplete="username"
          />
          <Button
            type="submit"
            variant="primary"
            label={{ key: "login.continue" }}
            disabled={username.isEmpty}
          />
        </VBox>
      </Form>
    );
  },
);

const SelectLoginUserForDevelopment: FC<{
  onSelectUser: (username: string, password: string) => void;
}> = ({ onSelectUser }) => {
  const users = [
    { name: "Kai Mossige - CLOUD VENTURES (admin)", u: "oslo", p: "norway" },
    {
      name: "Martin Lewandowski - CLOUD VENTURES (shareholder)",
      u: "04103849077",
      p: "dc0mpany4y0u",
    },
    { name: "Anette Tran - CLOUD VENTURES (board member)", u: "anette.tran", p: "dc0mpany4y0u" },
    { name: "Ingri Dyrkorn (Altinn test user)", u: "22124201644", p: "norway" },
    { name: "Fornuftig Pute (BR test user)", u: "24892649924", p: "norway" },
    { name: "Super admin", u: "superadmin", p: "dc0mpany4y0u" },
    { name: "Per Hasnotaccepted Terms", u: "per", p: "dc0mpany4y0u" },
    //unused for now, should clean up the next patch reset {name: "Naimdjon (user for searching 1881)", u: "naimdjon", p: "dc0mpany4y0u" },
    { name: "Foo Bar", u: "fb", p: "dc0mpany4y0u" },
  ];

  return (
    <VBox alignItems={"start"} gap={"md"}>
      <h4>Development mode only</h4>
      <VBox alignItems="start">
        {users.map((user) => (
          <LinkButton
            key={user.name}
            label={user.name}
            onClick={() => onSelectUser(user.u, user.p)}
            icon={"user-circle"}
          />
        ))}
      </VBox>
    </VBox>
  );
};

export const loginMethods: [method: LoginMethod, icon: LoginMethodIcon, label: TMessage][] =
  compact([
    [
      "norwegianBankId",
      { src: BankIDSymbol, darkSrc: BankIDSymbolDark },
      { key: "loginWithBankidNorway" },
    ],
    [
      "swedishBankId",
      { src: BankIDSwedenLogo, darkSrc: BankIDSwedenLogoDark },
      { key: "loginWithBankidSweden" },
    ],
    [
      "danishMitId",
      { src: MitIDDenmark, darkSrc: MitIDDenmarkDark },
      { key: "loginWithMitIdDenmark" },
    ],
    ["finnishFtn", { src: FinlandFlag }, { key: "loginWithFinnishFtn" }],
    ["microsoft", { src: MicrosoftLogo }, { key: "loginWithMicrosoft" }],
    ["google", { src: GoogleLogo }, { key: "loginWithGoogle" }],
  ]);

const ChooseLoginMethodView = observer(
  ({ model, customHeading }: { model: LoginModel; customHeading?: ReactNode }) => {
    const { t } = useTranslation();
    const queryParams = new URLSearchParams(window.location.search);
    const isLoginFailed = queryParams.get("error") === "loginfailed";
    const isAccountLocked = queryParams.get("error") === "account-locked";
    const isFromSignup = queryParams.get("referrer") === "signup";
    const visibleLoginMethods = isFromSignup
      ? loginMethods.filter(([method]) => !isSsoConnectedProvider(method))
      : loginMethods;
    const showUsernamePasswordLogin = !model.disableUsernamePasswordLogin && !isFromSignup;

    return (
      <>
        {model.loginStatus === "timed-out" ? (
          <Alert type={"warning"} message={t("login.timeout")} />
        ) : isAccountLocked ? (
          <Alert type={"error"} message={t("login.account-locked")} />
        ) : isLoginFailed ? (
          <Alert type={"error"} message={t("login.login-failed")} />
        ) : model.showBankIdWarning ? (
          <Alert message={{ key: "login.bankid-warning" }} type={"warning"} />
        ) : undefined}

        {customHeading ||
          (isFromSignup ? (
            <LoginHeader
              heading={{ key: "login.signup.title" }}
              intro={{ key: "login.signup.description" }}
            />
          ) : (
            <VBox textAlign="center">
              <h1 className="text-h3">{t("login.heading")}</h1>
            </VBox>
          ))}

        <ContentBox gap={"xl"}>
          <VBox gap={"md"}>
            {visibleLoginMethods.map(([method, icon, label]) => (
              <LoginMethodButton
                key={method}
                id={method}
                icon={icon}
                label={label}
                onClick={() => model.selectLoginMethod(method)}
                loading={model.activeLoginMethod === method}
              />
            ))}
          </VBox>
          {showUsernamePasswordLogin && (
            <>
              <LabelledDivider label={{ key: "login.choose-method-or" }} />
              <LoginWithUsername
                username={model.username}
                onContinue={() => model.selectLoginMethod("username")}
              />
            </>
          )}
        </ContentBox>

        {model.createUserModel && (
          <VBox textAlign={"center"} marginBottom={"sm"} marginTop={"lg"}>
            <strong>{t("invitation.no-bankid.title")}</strong>
            <Markdown
              value={t("invitation.no-bankid.description", {
                username: model.createUserModel.username.validatedValue,
              })}
              enableLinks
            />
            <Box justifyContent={"center"}>
              <Button
                variant={"secondary"}
                label={t("invitation.no-bankid.create-account")}
                onClick={() => model.selectLoginMethod("create-user")}
              />
            </Box>
          </VBox>
        )}

        {import.meta.env.DEV && !model.disableUsernamePasswordLogin && (
          <VBox alignItems="center" className={"mt-16"}>
            <SelectLoginUserForDevelopment onSelectUser={model.loginWithUsername} />
          </VBox>
        )}
      </>
    );
  },
);

const EnterPasswordView = observer(({ model }: { model: LoginModel }) => {
  const { t } = useTranslation();
  const isLoading = model.loginState === "loading";
  const isError = model.loginState === "error";
  const isThrottled = model.loginState === "throttled";
  const isAccountLocked = model.loginState === "account-locked";
  return (
    <>
      <LoginHeader
        heading={{ key: "login.enter-password.title" }}
        intro={{
          key: "login.enter-password.description",
          params: {
            username: model.username.validatedValue,
          },
        }}
      />
      <Form onSubmit={model.login}>
        <ContentBox gap={"md"}>
          <MTextInput
            name={"password"}
            label={t("login.password")}
            type={"password"}
            model={model.password}
            error={isError}
            message={isError ? t("login.incorrectData") : undefined}
            disabled={isLoading}
            autofocus
          />
          {isThrottled && (
            <Alert
              type={"error"}
              message={t("login.too-many-attempts", {
                count: Math.max(1, Math.ceil((model.throttledForSeconds ?? 900) / 60)),
              })}
            />
          )}
          {isAccountLocked && <Alert type={"error"} message={t("login.account-locked")} />}
          <Button
            type={"submit"}
            label={t("login.continue")}
            variant={"primary"}
            data-test-id="loginButton"
            loading={isLoading}
            fullWidth={true}
          />
        </ContentBox>
      </Form>
      <ContentBox alignItems="start">
        <LinkButton
          weight="normal"
          label={t("login.forgotPassword")}
          onClick={model.forgotPassword}
        />
      </ContentBox>
    </>
  );
});

const CreateUserView = observer(
  ({ model, createUserModel }: { model: LoginModel; createUserModel: CreateUserModel }) => {
    const { t } = useTranslation();
    const isLoading = model.loginState === "loading";
    const isError = model.loginState === "error";

    return (
      <>
        <LoginHeader heading={{ key: "login.register-user" }} />
        <Form onSubmit={() => model.createUser(createUserModel)}>
          <ContentBox gap={"lg"}>
            <VBox>
              <MTextInput
                label={t("login.username")}
                model={createUserModel.username}
                disabled={true}
              />
              <MTextInput label={t("name")} model={createUserModel.name} disabled={isLoading} />
              <MTextInput
                type="password"
                label={t("login.password")}
                model={createUserModel.password}
                disabled={isLoading}
              />
              <MTextInput
                type="password"
                label={t("login.repeat-password")}
                model={createUserModel.repeatPassword}
                disabled={isLoading}
              />
            </VBox>
            <Button
              label={t("register")}
              variant={"primary"}
              data-test-id="loginButton"
              loading={isLoading}
              disabled={isLoading}
              fullWidth={true}
              type={"submit"}
            />
            {isError && <Alert message={{ key: "general-error" }} type={"error"} />}
          </ContentBox>
        </Form>
      </>
    );
  },
);

const MfaChallengeView = observer(
  ({ model, challenge }: { model: LoginModel; challenge: MfaChallenge }) => {
    const { t } = useTranslation();
    return (
      <>
        <LoginHeader
          heading={{ key: "login.one-time-code" }}
          intro={
            <>
              {model.mfaState === "invalid-code" || model.mfaState === "sending-new-code" ? (
                <Alert
                  type={"error"}
                  message={
                    <VBox>
                      <div>{t("login.invalid-one-time-code")}</div>
                      <p>
                        <LinkButton
                          label={t("login.get-a-new-code")}
                          color={"inherit"}
                          onClick={model.sendNewOneTimeCode}
                          loading={model.mfaState === "sending-new-code"}
                        />
                      </p>
                    </VBox>
                  }
                />
              ) : model.mfaState === "resend-throttled" ? (
                <Alert
                  type={"error"}
                  message={
                    <VBox>
                      <div>
                        {t("login.too-many-code-requests", {
                          count: Math.max(1, Math.ceil((model.throttledForSeconds ?? 60) / 60)),
                        })}
                      </div>
                      <p>
                        <LinkButton
                          label={t("login.get-a-new-code")}
                          color={"inherit"}
                          onClick={model.sendNewOneTimeCode}
                        />
                      </p>
                    </VBox>
                  }
                />
              ) : model.mfaState === "account-locked" ? (
                <Alert type={"error"} message={t("login.account-locked")} />
              ) : model.mfaState === "unknown-error" ? (
                <Alert type={"error"} message={t("login.failed-to-send-code")} />
              ) : (
                <p>
                  {t("login.one-time-code-has-been-sent--email", { email: challenge.recipient })}
                </p>
              )}
            </>
          }
        />

        <Form submitOnEnter onSubmit={model.submitOneTimeCode}>
          <ContentBox gap={"sm"}>
            <MTextInput
              label={t("login.one-time-code")}
              model={model.mfaToken}
              error={model.mfaState === "invalid-code"}
              autofocus
              autoComplete="one-time-code"
            />
            <Button
              label={t("submit")}
              variant={"primary"}
              type={"submit"}
              loading={model.mfaState === "submitting"}
              disabled={model.mfaState === "submitting"}
            />
          </ContentBox>
        </Form>
      </>
    );
  },
);

const ResetPasswordView = observer(({ model }: { model: LoginModel }) => {
  const { t } = useTranslation();
  return (
    <>
      <LoginHeader
        heading={{ key: "reset-password.title" }}
        intro={
          model.passwordResetRequestState === "sent"
            ? undefined
            : { key: "reset-password.description" }
        }
      />
      <ContentBox>
        {model.passwordResetRequestState === "sent" ? (
          <Alert
            icon
            message={t("reset-password.sent", { email: model.email.validatedValue })}
            type={"success"}
          />
        ) : (
          <Form onSubmit={model.resetPassword}>
            <VBox gap={"md"}>
              <MTextInput label={t("reset-password.email")} model={model.email} autofocus />
              <Button
                label={t("reset-password.send")}
                variant={"primary"}
                type={"submit"}
                loading={model.passwordResetRequestState === "submitting"}
                disabled={model.passwordResetRequestState === "submitting"}
              />
              {model.passwordResetRequestState === "server-error" && (
                <Alert message={t("reset-password.could-not-send")} type={"error"} />
              )}
            </VBox>
          </Form>
        )}
      </ContentBox>
    </>
  );
});

export const LoginView = observer(
  ({ model, customHeading }: { model: LoginModel; customHeading?: ReactNode }) => {
    useEffect(() => {
      model.initAffiliateKey();
    }, [model]);

    useEffect(() => {
      model.initRedirectUrl();
    }, [model]);

    useEffect(() => {
      model.checkBankIdStatus();
    }, [model]);

    switch (true) {
      case isMfaChallenge(model.loginStatus):
        return (
          <LoginLayout onBack={model.clearLoginMethod}>
            <MfaChallengeView challenge={model.loginStatus} model={model} />
          </LoginLayout>
        );
      case model.loginView === "forgot-password":
        return (
          <LoginLayout onBack={model.clearLoginMethod}>
            <ResetPasswordView model={model} />
          </LoginLayout>
        );
      case model.loginView === "username":
        return (
          <LoginLayout onBack={model.clearLoginMethod}>
            <EnterPasswordView model={model} />
          </LoginLayout>
        );
      case model.loginView === "create-user" && model.createUserModel !== undefined:
        return (
          <LoginLayout onBack={model.clearLoginMethod}>
            <CreateUserView model={model} createUserModel={model.createUserModel} />
          </LoginLayout>
        );
      default:
        return (
          <LoginLayout>
            <ChooseLoginMethodView model={model} customHeading={customHeading} />
          </LoginLayout>
        );
    }
  },
);
